// src/state/pinia/site.js
//
// Store witryn dla LessSEO. Po collapse warstwy Project witryna jest centralną
// jednostką: topbar przełącza witryny, uprawnienia są kluczowane jej uuid.
// Źródłem prawdy jest payload auth (user.sites / user.site_permissions) —
// nie ma osobnego endpointu „projektów".

import { defineStore } from "pinia";
import { useAuthStore } from "@/state/pinia/auth";
import api from "@/api/api";

function storageKey() {
    try {
        const user = JSON.parse(localStorage.getItem("user") || "{}");
        return `activeSite_${user?.uuid || "default"}`;
    } catch {
        return "activeSite_default";
    }
}

export const useSiteStore = defineStore("site", {
    state: () => ({
        sites: [],
        activeSite: null,
        activeSiteObj: null,
        linkedSystemsBySite: {}, // array of linked sibling-system projects, keyed by site uuid
        cmsConnectedBySite: {}, // czy witryna ma dokąd publikować, keyed by site uuid
    }),

    getters: {
        activeSiteUuid: (state) => state.activeSite,
        hasActiveSite: (state) => !!state.activeSite,
        activeLinkedSystems: (state) =>
            state.activeSite ? (state.linkedSystemsBySite[state.activeSite] || []) : [],

        // Planowanie postów bez podpiętego CMS-a nie ma dokąd publikować —
        // menu trzyma się tej samej odpowiedzi co sama publikacja.
        hasLinkedCms: (state) =>
            state.activeSite ? !!state.cmsConnectedBySite[state.activeSite] : false,

        // Witryny pogrupowane po workspace (do switchera w topbarze).
        // Zawiera WSZYSTKIE workspace'y do których user ma dostęp (z payloadu
        // auth), także te bez witryn — inaczej pusty workspace „znika" ze
        // switchera i user nie ma jak do niego wejść, by dodać pierwszą witrynę.
        sitesGroupedByWorkspace: (state) => {
            const auth = useAuthStore();
            const wsMap = auth.getUser()?.workspaces || {};
            const grouped = {};

            // Najpierw zainicjuj każdy workspace z dostępu (również puste).
            for (const [ws, data] of Object.entries(wsMap)) {
                grouped[ws] = {
                    workspace_uuid: ws,
                    workspace_name: (typeof data === "object" ? data?.name : data) || "Workspace",
                    sites: [],
                };
            }

            // Następnie podczep witryny pod ich workspace.
            for (const site of state.sites) {
                const ws = site.workspace_uuid || "_";
                if (!grouped[ws]) {
                    grouped[ws] = {
                        workspace_uuid: ws,
                        workspace_name: wsMap[ws]?.name || "Workspace",
                        sites: [],
                    };
                }
                grouped[ws].sites.push(site);
            }
            return Object.values(grouped);
        },
    },

    actions: {
        // Klucz localStorage z ostatnią witryną usera (czyta też router).
        storageKey() {
            return storageKey();
        },

        // Populacja z payloadu auth (mapa {uuid: {name, domain, workspace_uuid}}).
        loadFromAuth() {
            const user = useAuthStore().getUser();
            const map = user?.sites || {};
            this.sites = Object.entries(map).map(([uuid, s]) => ({ uuid, ...s }));

            if (this.sites.length === 0) {
                this.setActive(null);
                return;
            }

            const stored = localStorage.getItem(storageKey());
            if (this.sites.length === 1) {
                this.setActive(this.sites[0].uuid);
            } else if (stored && this.sites.find((s) => s.uuid === stored)) {
                this.setActive(stored);
            } else {
                this.setActive(this.sites[0].uuid);
            }
        },

        setActive(uuid) {
            this.activeSite = uuid;
            const key = storageKey();
            if (uuid) {
                localStorage.setItem(key, uuid);
                this.activeSiteObj = this.sites.find((s) => s.uuid === uuid) || null;
                // Workspace kontekstu = workspace aktywnej witryny.
                if (this.activeSiteObj?.workspace_uuid) {
                    useAuthStore().setActiveWorkspace(this.activeSiteObj.workspace_uuid);
                }
                this.fetchLinkedSystems(uuid);
            } else {
                localStorage.removeItem(key);
                this.activeSiteObj = null;
            }
        },

        // Linked sibling-system projects (cms / commerce) for the active site —
        // drives the bottom system switcher. Soft-fail to an empty list.
        async fetchLinkedSystems(uuid, force = false) {
            const id = uuid || this.activeSite;
            if (!id) return;
            if (!force && this.linkedSystemsBySite[id] !== undefined) return;
            try {
                const res = await api.get(`api/sites/${id}/linked-systems`);
                this.linkedSystemsBySite[id] = res?.data?.data ?? [];
                this.cmsConnectedBySite[id] = !!res?.data?.cms_connected;
            } catch {
                this.linkedSystemsBySite[id] = [];
                this.cmsConnectedBySite[id] = false;
            }
        },

        // Po dodaniu witryny w modalu — dorzuć i ustaw aktywną.
        addSite(site) {
            if (!site?.uuid) return;
            const shape = {
                uuid: site.uuid,
                name: site.name,
                domain: site.domain,
                workspace_uuid: site.workspace_uuid,
            };
            if (!this.sites.find((s) => s.uuid === shape.uuid)) {
                this.sites.push(shape);
            }
            this.setActive(shape.uuid);
        },

        reset() {
            this.sites = [];
            this.activeSite = null;
            this.activeSiteObj = null;
            this.linkedSystemsBySite = {};
        },
    },
});
